@let selectedId = selectedConversationId$ | async;
@let conversations = (conversations$ | async) ?? [];
@let hasError = error$ | async;
@let editingId = editingConversationId$ | async;

<nimbus-collapsible-sidebar-panel
  [title]="TranslationKeys.CompanionChatPageHistoryTitle | translate"
  [titleIcon]="ICONS.COMPANION"
  [expandAriaLabel]="TranslationKeys.CompanionChatPageHistoryExpand | translate"
  [collapseAriaLabel]="TranslationKeys.CompanionChatPageHistoryCollapse | translate"
  [loading]="loading$ | async"
  [isEmpty]="!hasError && !conversations.length"
  [emptyIcon]="ICONS.SEARCH_LIGHT"
  [emptyText]="TranslationKeys.CompanionChatPageHistoryEmpty | translate"
  (isCollapsedChange)="onCollapsedChange($event)"
>
  <div panel-header>
    <button
      class="history-new-chat"
      [class.active]="selectedId === null"
      [class.collapsed]="isCollapsed"
      [disabled]="selectedId === null"
      [attr.aria-label]="isCollapsed ? (TranslationKeys.CompanionChatPageHistoryNewChat | translate) : null"
      (click)="selectConversation(null)"
    >
      <i [nimbusIcon]="ICONS.ADD"></i>
      @if (!isCollapsed) {
        {{ TranslationKeys.CompanionChatPageHistoryNewChat | translate }}
      }
    </button>

    @if (!isCollapsed && (conversations.length || searchControl.value)) {
      <nimbus-input-search class="history-search" [formControl]="searchControl"></nimbus-input-search>
    }

    @if (hasError && !isCollapsed) {
      <div class="history-error">
        <span class="history-error-text">{{ TranslationKeys.CompanionChatPageHistoryError | translate }}</span>
        <button nimbusSecondaryButton (click)="retryLoad()">
          {{ TranslationKeys.CompanionChatPageHistoryRetry | translate }}
        </button>
      </div>
    }
  </div>

  <div
    panel-content
    #historyList
    class="history-list"
    infiniteScroll
    [infiniteScrollDistance]="1"
    [infiniteScrollThrottle]="100"
    [scrollWindow]="false"
    [alwaysCallback]="true"
    (resized)="onResized()"
    (scrolled)="loadMore()"
  >
    @for (conversation of conversations; track conversation.conversationId) {
      @if (!isCollapsed) {
        <div
          class="history-item"
          [class.active]="selectedId === conversation.conversationId"
          [class.editing]="editingId === conversation.conversationId"
          [attr.aria-label]="conversation.title"
          [title]="conversation.title"
          (click)="selectConversation(conversation.conversationId)"
        >
          @if (editingId === conversation.conversationId) {
            <nimbus-input-counter class="history-item-editing-wrapper">
              <div class="history-item-content">
                <input
                  #renameInput
                  class="history-item-rename-input"
                  [formControl]="renameControl"
                  [nimbusInputCounter]="renameControl.value"
                  [maxLength]="60"
                  [attr.maxlength]="60"
                  (click)="$event.stopPropagation()"
                  (keydown.enter)="confirmRename(conversation)"
                  (keydown.escape)="cancelRename()"
                />
              </div>
              <div class="history-item-action-btns">
                <button
                  class="history-item-action"
                  [disabled]="isRenameDisabled(conversation)"
                  (click)="$event.stopPropagation(); confirmRename(conversation)"
                >
                  <i [nimbusIcon]="ICONS.CHECK"></i>
                </button>
                <button class="history-item-action" (click)="$event.stopPropagation(); cancelRename()">
                  <i [nimbusIcon]="ICONS.CLOSE"></i>
                </button>
              </div>
            </nimbus-input-counter>
          } @else {
            <div class="history-item-content">
              <span class="history-item-preview">{{ conversation.title }}</span>
              <span class="history-item-date">{{ getConversationDateLabel(conversation.updatedAt) }}</span>
            </div>
            <button
              class="history-item-more"
              [matMenuTriggerFor]="conversationMenu"
              [matMenuTriggerData]="{ conversation: conversation }"
              [attr.aria-label]="TranslationKeys.CompanionChatPageHistoryMenuLabel | translate"
              (click)="$event.stopPropagation()"
            >
              <i [nimbusIcon]="ICONS.ELLIPSIS_H"></i>
            </button>
          }
        </div>
      }
    }
  </div>

  <div panel-footer>
    @if (conversations.length && !isCollapsed) {
      <button class="history-clear-btn" (click)="deleteConversation()">
        {{ TranslationKeys.CompanionChatPageHistoryClearHistory | translate }}
      </button>
    }
  </div>
</nimbus-collapsible-sidebar-panel>

<mat-menu #conversationMenu="matMenu" class="companion-history-context-menu">
  <ng-template matMenuContent let-conversation="conversation">
    <button mat-menu-item (click)="renameConversation(conversation)">
      <i [nimbusIcon]="ICONS.EDIT"></i>
      <span>{{ TranslationKeys.GeneralRename | translate }}</span>
    </button>
    <button mat-menu-item class="destructive" (click)="deleteConversation(conversation)">
      <i [nimbusIcon]="ICONS.TRASH_LIGHT"></i>
      <span>{{ TranslationKeys.GeneralDelete | translate }}</span>
      <nimbus-tooltip [text]="TranslationKeys.GeneralDelete | translate"></nimbus-tooltip>
    </button>
  </ng-template>
</mat-menu>
